import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE = 'https://show-evenement.com';
const SITE_NAME = 'Show Événement';
const DEFAULT_IMG = `${SITE}/og-image.jpg`;
const DEFAULT_DESC = 'Location de matériel événementiel à Figeac — Lot (46). Photobooth, jeux gonflables, animations gourmandes, mobilier. Livraison dans tout le Lot, Aveyron, Cantal. Devis gratuit.';

// ─── Schéma LocalBusiness global ─────────────────────────────────────────────
export const LOCAL_BUSINESS_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  '@id': `${SITE}/#localbusiness`,
  name: 'Show Événement',
  description: 'Spécialiste de la location de matériel événementiel à Figeac. Photobooth, jeux gonflables, animations gourmandes, mobilier. Devis gratuit.',
  url: SITE,
  logo: `${SITE}/logo.png`,
  image: DEFAULT_IMG,
  telephone: '+33565000000',
  email: 'contact@show-evenement.com',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Figeac',
    addressLocality: 'Figeac',
    postalCode: '46100',
    addressRegion: 'Lot',
    addressCountry: 'FR',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 44.6039,
    longitude: 2.0293,
  },
  areaServed: [
    { '@type': 'City', name: 'Figeac' },
    { '@type': 'AdministrativeArea', name: 'Lot' },
    { '@type': 'AdministrativeArea', name: 'Aveyron' },
    { '@type': 'AdministrativeArea', name: 'Cantal' },
    { '@type': 'AdministrativeArea', name: 'Corrèze' },
  ],
  openingHoursSpecification: [
    { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday'], opens: '09:00', closes: '18:00' },
    { '@type': 'OpeningHoursSpecification', dayOfWeek: 'Saturday', opens: '09:00', closes: '17:00' },
  ],
  sameAs: [
    'https://www.facebook.com/showevenement',
    'https://www.instagram.com/showevenement',
  ],
  priceRange: '€€',
};

/**
 * SEO — Composant universel de balises méta
 * Props :
 *   title        : Titre de la page (sans le suffixe Show Événement)
 *   description  : Description unique (160 car max)
 *   canonical    : URL canonique absolue (ex: https://show-evenement.com/catalogue)
 *   image        : URL absolue de l'image OG
 *   type         : 'website' | 'product' | 'article'
 *   noindex      : bool — true pour les pages admin/privées
 *   schema       : objet JSON-LD supplémentaire (Product, Article…)
 *   breadcrumb   : array [{name, url}] pour BreadcrumbList
 */
export default function SEO({
  title,
  description = DEFAULT_DESC,
  canonical,
  image = DEFAULT_IMG,
  type = 'website',
  noindex = false,
  schema = null,
  breadcrumb = null,
}) {
  const fullTitle = title
    ? `${title} | Show Événement — Figeac (46)`
    : 'Show Événement — Location matériel événementiel Figeac, Lot (46)';
  const canonicalUrl = canonical || SITE;
  const imageAbsolute = image?.startsWith('http') ? image : `${SITE}${image}`;

  // Schéma BreadcrumbList
  const breadcrumbSchema = breadcrumb?.length ? {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Accueil', item: SITE },
      ...breadcrumb.map((b, i) => ({
        '@type': 'ListItem',
        position: i + 2,
        name: b.name,
        item: b.url,
      })),
    ],
  } : null;

  return (
    <Helmet>
      {/* ── Titre ── */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />

      {/* ── Indexation ── */}
      {noindex
        ? <meta name="robots" content="noindex, nofollow" />
        : <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
      }

      {/* ── Canonical ── */}
      <link rel="canonical" href={canonicalUrl} />

      {/* ── Open Graph ── */}
      <meta property="og:title"       content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url"         content={canonicalUrl} />
      <meta property="og:type"        content={type === 'product' ? 'product' : 'website'} />
      <meta property="og:image"       content={imageAbsolute} />
      <meta property="og:image:width"  content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name"   content={SITE_NAME} />
      <meta property="og:locale"      content="fr_FR" />

      {/* ── Twitter Card ── */}
      <meta name="twitter:card"        content="summary_large_image" />
      <meta name="twitter:title"       content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image"       content={imageAbsolute} />

      {/* ── Geo local ── */}
      <meta name="geo.region"   content="FR-46" />
      <meta name="geo.placename" content="Figeac" />
      <meta name="geo.position" content="44.6039;2.0293" />
      <meta name="ICBM"         content="44.6039, 2.0293" />

      {/* ── JSON-LD LocalBusiness (toutes les pages) ── */}
      <script type="application/ld+json">
        {JSON.stringify(LOCAL_BUSINESS_SCHEMA)}
      </script>

      {/* ── JSON-LD schema supplémentaire (Product, Article…) ── */}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}

      {/* ── BreadcrumbList ── */}
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
    </Helmet>
  );
}
